// Smart Daara - YouTube Player with Automatic Progress Tracking
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Play, Pause, RotateCcw, CheckCircle, Volume2, VolumeX } from 'lucide-react';

// Load YouTube IFrame API
const loadYouTubeAPI = () => {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT);
    };
  });
};

export function YouTubePlayer({ 
  videoId, 
  onProgressUpdate, 
  initialProgress = 0,
  language = 'fr',
  className = '',
  isStudent = false
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const maxWatchedRef = useRef(0);
  
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(initialProgress);
  const [isMuted, setIsMuted] = useState(false);

  // Initialize player
  useEffect(() => {
    let mounted = true;

    const initPlayer = async () => {
      const YT = await loadYouTubeAPI();
      
      if (!mounted || !containerRef.current) return;

      // Create unique ID
      const playerId = `youtube-player-${videoId}-${Date.now()}`;
      containerRef.current.id = playerId;

      playerRef.current = new YT.Player(playerId, {
        videoId: videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          fs: 0,
          playsinline: 1
        },
        events: {
          onReady: (event) => {
            if (!mounted) return;
            setIsReady(true);
            setDuration(event.target.getDuration());
            
            // Start at last position if resuming
            const total = event.target.getDuration();
            maxWatchedRef.current = (Math.min(initialProgress, 100) / 100) * total;
            if (initialProgress > 0 && initialProgress < 100) {
              const startTime = (initialProgress / 100) * total;
              event.target.seekTo(startTime, true);
            }
          },
          onStateChange: (event) => {
            if (!mounted) return;
            
            if (event.data === YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              startProgressTracking();
            } else if (event.data === YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              stopProgressTracking();
            } else if (event.data === YT.PlayerState.ENDED) {
              setIsPlaying(false);
              stopProgressTracking();
              handleProgressUpdate(100);
            }
          }
        }
      });
    };

    initPlayer();

    return () => {
      mounted = false;
      stopProgressTracking();
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // Player already destroyed
        }
      }
    };
  }, [videoId]);

  const startProgressTracking = useCallback(() => {
    stopProgressTracking();
    
    progressIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        try {
          const current = playerRef.current.getCurrentTime();
          const total = playerRef.current.getDuration();
          
          if (total > 0) {
            const newProgress = Math.min(100, (current / total) * 100);
            setCurrentTime(current);
            setProgress(newProgress);
            
            // Report progress at key points (every 10%)
            const progressThresholds = [10, 25, 50, 75, 90, 95, 100];
            for (const threshold of progressThresholds) {
              if (newProgress >= threshold && progress < threshold) {
                handleProgressUpdate(threshold);
                break;
              }
            }
          }
        } catch (e) {
          // Player not ready
        }
      }
    }, 1000);
  }, [progress]);

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleProgressUpdate = useCallback((newProgress) => {
    if (onProgressUpdate) {
      onProgressUpdate(newProgress, currentTime, duration);
    }
  }, [onProgressUpdate, currentTime, duration]);

  const togglePlay = () => {
    if (!playerRef.current) return;
    
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    
    if (isMuted) {
      playerRef.current.unMute();
    } else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  };

  const restart = () => {
    if (!playerRef.current) return;
    playerRef.current.seekTo(0, true);
    setCurrentTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const markComplete = () => {
    handleProgressUpdate(100);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Video Container */}
      <div className="relative aspect-video bg-black rounded-t-lg overflow-hidden">
        <div ref={containerRef} className="w-full h-full" />
        {/* Overlay: blocks direct YouTube interaction — play/pause only via app controls */}
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={togglePlay}
          data-testid="youtube-click-shield"
          title={language === 'fr' ? 'Utilisez les commandes ci-dessous' : 'Use the controls below'}
        >
          {!isPlaying && isReady && (
            <div className="w-full h-full flex items-center justify-center bg-black/30 transition-opacity">
              <div className="w-16 h-16 rounded-full bg-emerald-600/90 flex items-center justify-center shadow-lg">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-100 p-3 border-b">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-600">
            {language === 'fr' ? 'Progression automatique' : 'Automatic Progress'}
          </span>
          <span className="font-semibold text-emerald-700">
            {Math.round(progress)}%
          </span>
        </div>
        <Progress value={progress} className="h-2" />
        
        {/* Time display */}
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 mt-3">
          <Button
            size="sm"
            variant="outline"
            onClick={togglePlay}
            disabled={!isReady}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={toggleMute}
            disabled={!isReady}
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={restart}
            disabled={!isReady}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <div className="flex-1" />

          <Button
            size="sm"
            onClick={markComplete}
            className={`${isStudent ? 'bg-gray-400 cursor-not-allowed opacity-50' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
            disabled={progress >= 100 || isStudent}
            title={isStudent ? (language === 'fr' ? 'Réservé aux enseignants' : 'Teachers only') : ''}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {progress >= 100 
              ? (language === 'fr' ? 'Terminé' : 'Completed')
              : (language === 'fr' ? 'Marquer terminé' : 'Mark Complete')
            }
          </Button>
        </div>
        
        <p className="text-xs text-slate-500 mt-2">
          {language === 'fr' 
            ? '💡 La progression est enregistrée automatiquement pendant la lecture'
            : '💡 Progress is tracked automatically while watching'
          }
        </p>
      </div>
    </div>
  );
}

// Helper to extract YouTube video ID
export function extractYouTubeId(url) {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

export default YouTubePlayer;
